/* eslint-disable no-undef */
import SecureLocalStorage from 'secure-ls';
import { ApolloClient } from 'apollo-client';
import { setContext } from 'apollo-link-context';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { createUploadLink } from 'apollo-upload-client';
import { onError } from '@apollo/client/link/error';
import { GET_WORKSPACE_INFO, GET_STAFFS_ROLES } from '../modules/shared/graphql';
import { initialize } from 'launchdarkly-js-client-sdk';
import { createLdClientWithFallback } from '../modules/shared/featureFlags';
import { getStripeKeyByCountry } from './fitunePayCountries';
import _ from 'lodash';
import moment from 'moment';
import cart from './modules/cart';
import * as Sentry from '@sentry/vue';

const storage = new SecureLocalStorage();

// `gon` is injected by the authenticated layout and is not present on every
// page (e.g. unauthenticated auth/onboarding packs that also load this module
// transitively). Fall back to an empty base URL so import doesn't throw.
const gonBaseUrl = typeof gon !== 'undefined' ? gon.baseUrl : '';

const httpLink = createUploadLink({
  uri: `${gonBaseUrl}/graphql`
});

const authLink = setContext((_, { headers }) => {
  // return the headers to the context so httpLink can read them
  const workspace = localStorage.getItem('workspace');
  return {
    headers: {
      ...headers,
      Authorization: gon.user.token,
      uid: gon.user.uid,
      ...(workspace ? { 'X-Workspace': `${workspace}` } : {})
    }
  };
});

const errorLink = onError(({ networkError, operation }) => {
  if (networkError && networkError.statusCode === 403) {
    window.location = '/admin/app/websites';
  } else if (networkError?.statusCode === 504) {
    const operationName = operation?.operationName || 'UnnamedOperation';
    const variables = operation?.variables || {};

    Sentry.captureException(new Error('GraphQL API request timed out'), {
      tags: { timeout: true, graphql: true },
      extra: {
        operationName,
        variables,
        query: operation?.query?.loc?.source?.body || 'Query body not available'
      }
    });
  }
});

const defaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'ignore'
  },
  query: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all'
  }
};

const apolloClient = new ApolloClient({
  link: errorLink.concat(authLink.concat(httpLink)),
  cache: new InMemoryCache({
    addTypename: false
  }),
  defaultOptions: defaultOptions,
  queryDeduplication: false
});

apolloClient.stop();

export { apolloClient };

export default {
  state: {
    baseUrl: null,
    loading: false,
    user: {},
    hasMultipleWorkspaces: false,
    workspace: {
      role: '',
      ownerPlan: {},
      currency: {}
    },
    stepOnboarding: null,
    website: {
      customDomainActive: false,
      customDomainURL: '',
      fituneDomainURL: '',
      // Online branch id, needed by every surface that updates the branch
      // (e.g. changing the coach language).
      branchId: null,
      // Currency country code from the coach currency. Distinct from
      // `workspace.ownerPlan.countryCode`, which comes from the region.
      currencyCountryCode: ''
    },
    show1On1: true,
    scheduleDays: 30,
    ldClient: null,
    maxPosition: 0,
    toggleCalendar: 'on',
    venuesConnectedWellhub: 0,
    venuesConnected1Fit: 0,
    venuesConnectedTotalPass: 0,
    venuesConnectedFitpass: 0,
    venuesConnectedGoogleReserve: 0,
    googleReserveIntegrationData: null,
    kiosk: localStorage.getItem('kiosk')
      ? JSON.parse(localStorage.getItem('kiosk'))
      : {
          enabled: false,
          id: null,
          redirectKiosk: false
        },
    paywallOverride: {
      klassesLimitation: null,
      plansLimitation: null
    },
    invoiceUrl: '',
    migrationInProgress: false,
    // Application-shell feature flags. `null` means unresolved, so the shell can
    // avoid rendering chrome before it knows which variant to paint.
    revampShellFlag: null
  },
  mutations: {
    baseUrl(state, value) {
      state.baseUrl = value;

      storage.set('baseUrl', state.baseUrl);
    },
    user(state, value) {
      state.user = value;
      storage.set('token', state.user.token);
      storage.set('uid', state.user.uid);
    },
    stepOnboarding(state, value) {
      state.stepOnboarding = value;
      storage.set('stepOnboarding', state.stepOnboarding);
    },
    setWebsiteInfo(state, payload) {
      let toggleCalendar = localStorage.getItem('toggleCalendar');
      if (!toggleCalendar || !['on', 'off'].includes(toggleCalendar)) {
        toggleCalendar = 'on';
        localStorage.setItem('toggleCalendar', toggleCalendar);
      }
      // Merge rather than replace: callers send partial payloads. DomainSettings
      // refreshes the domain fields only, and must not drop keys it does not know
      // about (branchId, currencyCountryCode).
      state.website = { ...state.website, ...payload };
      state.toggleCalendar = toggleCalendar;
      const realClient = initialize(
        process.env.VUE_APP_LAUNCHDARKLY_CLIENTSIDE_ID,
        {
          kind: 'workspace',
          key: payload.subdomain,
          email: payload.email,
          id: payload.id,
          toggleCalendar: toggleCalendar
        },
        {
          sendEventsOnlyForVariation: true
        }
      );
      state.ldClient = createLdClientWithFallback(realClient);
    },
    setWorkspace(state, value) {
      const newValue = { ...value };
      const { ownerPlan, staffShowAnalyticsFlag, fitunePay } = state.workspace;
      const { fitunePayCountryCode, fitunePayExpansion } = state.workspace;

      // Extra owner plan keys
      // `roledStaffsLimit` is null both while unresolved and when the plan is
      // unlimited, so the generic null check is enough to keep a resolved limit.
      const ownerPlanKeys = [
        'roledStaffsLimit',
        'activeUsersLimit',
        'rooms',
        'vimeo',
        'wellhubEnabled',
        'onefitEnabled',
        'totalpassEnabled',
        'googleReserveEnabled',
        'fitpassEnabled'
      ];

      ownerPlanKeys.forEach(key => {
        if (ownerPlan[key] !== null) {
          newValue.ownerPlan[key] = ownerPlan[key];
        }
      });

      if (staffShowAnalyticsFlag !== null) {
        newValue.staffShowAnalyticsFlag = staffShowAnalyticsFlag;
      }
      if (fitunePay !== null) {
        newValue.fitunePay = fitunePay;
      }
      // Preserve the Fitune Pay fields committed by Header: getWorkspace re-runs on every
      // navigation (online router beforeEach) and would otherwise wipe them.
      if (fitunePayCountryCode !== undefined) {
        newValue.fitunePayCountryCode = fitunePayCountryCode;
      }
      if (fitunePayExpansion !== undefined) {
        newValue.fitunePayExpansion = fitunePayExpansion;
      }

      state.workspace = newValue;
    },
    setRoledStaffsLimit(state, value) {
      state.workspace.ownerPlan.roledStaffsLimit = value;
    },
    setActiveUsersLimit(state, value) {
      state.workspace.ownerPlan.activeUsersLimit = value;
    },
    setHasMultipleWorkspaces(state, value) {
      state.hasMultipleWorkspaces = value;
    },
    setRooms(state, value) {
      state.workspace.ownerPlan.rooms = value;
    },
    setVenuesConnectedWellhub(state, value) {
      state.venuesConnectedWellhub = value;
    },
    setVenuesConnected1Fit(state, value) {
      state.venuesConnected1Fit = value;
    },
    setVenuesConnectedTotalPass(state, value) {
      state.venuesConnectedTotalPass = value;
    },
    setVenuesConnectedFitpass(state, value) {
      state.venuesConnectedFitpass = value;
    },
    setVenuesConnectedGoogleReserve(state, value) {
      state.venuesConnectedGoogleReserve = value;
    },
    setGoogleReserveIntegrationData(state, value) {
      state.googleReserveIntegrationData = value;
    },
    setStaffSetting(state, value) {
      state.workspace.staff.staffSetting = value;
    },
    setFitunePay(state, value) {
      state.workspace.fitunePay = value;
    },
    setFitunePayCountryCode(state, value) {
      state.workspace.fitunePayCountryCode = value;
    },
    setFitunePayExpansion(state, value) {
      state.workspace.fitunePayExpansion = value;
    },
    setVimeo(state, value) {
      state.workspace.ownerPlan.vimeo = value;
    },
    setWellhub(state, value) {
      state.workspace.ownerPlan.wellhubEnabled = value;
    },
    setOneFit(state, value) {
      state.workspace.ownerPlan.onefitEnabled = value;
    },
    setTotalPass(state, value) {
      state.workspace.ownerPlan.totalpassEnabled = value;
    },
    setFitpass(state, value) {
      state.workspace.ownerPlan.fitpassEnabled = value;
    },
    setGoogleReserve(state, value) {
      state.workspace.ownerPlan.googleReserveEnabled = value;
    },
    setExtraInfoUser(state, payload) {
      state.user = { ...state.user, ...payload };
    },
    setUserEmail(state, value) {
      state.user.uid = value;
    },
    setUserFullname(state, value) {
      state.user.fullname = value;
    },
    set1On1Tab(state, payload) {
      state.show1On1 = payload;
    },
    setScheduleDays(state, payload) {
      state.scheduleDays = payload;
    },
    setMaxPosition(state, payload) {
      state.maxPosition = payload;
    },
    setKiosk(state, payload) {
      state.kiosk = payload;
      localStorage.setItem('kiosk', JSON.stringify(payload));
    },
    setEmbedInfoSeen(state, payload) {
      state.workspace.staff.embedInfoSeen = payload || false;
    },
    setPaywallOverride(state, plan) {
      let klassesLimitation;
      let plansLimitation;

      if (
        plan.paywallOverride &&
        (plan.paywallOverride.klassLimitation === null || plan.paywallOverride.klassLimitation > 0)
      ) {
        klassLimitation = plan.paywallOverride.klassLimitation;
      } else {
        klassesLimitation = plan.fituPrice.fituProduct.klassesLimit;
      }
      state.paywallOverride.klassesLimitation = klassesLimitation;

      if (
        plan.paywallOverride &&
        (plan.paywallOverride.providerPlansLimit === null ||
          plan.paywallOverride.providerPlansLimit > 0)
      ) {
        plansLimitation = plan.paywallOverride.providerPlansLimit;
      } else {
        plansLimitation = plan.fituPrice.fituProduct.providerPlansLimit;
      }
      state.paywallOverride.plansLimitation = plansLimitation;
    },
    setInvoiceUrl(state, value) {
      state.invoiceUrl = value;
    },
    setMigrationInProgress(state, value) {
      state.migrationInProgress = value;
    },
    setRevampShellFlag(state, value) {
      state.revampShellFlag = value;
    }
  },
  actions: {
    async getWorkspace({ commit }) {
      try {
        const [workspaceRes, staffsRes] = await Promise.all([
          apolloClient.query({ query: GET_WORKSPACE_INFO }),
          apolloClient.query({ query: GET_STAFFS_ROLES })
        ]);
        const res = { data: { ...workspaceRes.data, ...staffsRes.data } };
        const workspace = res.data.coach.providerWorkspace;
        const partnersSubscriptions = workspace.workspace.partnersSubscriptions;
        // eslint-disable-next-line no-prototype-builtins
        const role = workspace.hasOwnProperty('role') ? workspace.role : null;
        const currentPlan = _.maxBy(partnersSubscriptions, 'fituPrice.amount');
        commit('setPaywallOverride', currentPlan);
        const ownerPlan =
          currentPlan && Object.keys(currentPlan).length > 0
            ? {
                id: currentPlan.id,
                status: currentPlan.stripeStatus,
                slug: currentPlan.fituPrice.fituProduct.slug.replace(/_v\d+(_\d+)*$/, ''),
                label: currentPlan.fituPrice.fituProduct.label,
                priceId: currentPlan.fituPrice.id,
                priceInterval: currentPlan.fituPrice.interval,
                // Value of attributes with // * will be set in Header.vue
                roledStaffsLimit: null, // * null means unlimited staff access
                activeUsersLimit: null, // * null means unlimited active clients
                rooms: null, // *
                vimeo: null, // *
                wellhubEnabled: null, // *
                onefitEnabled: null, // *
                totalpassEnabled: null, // *
                fitpassEnabled: null, // *
                usedRoles: res.data.staffs.collection.filter(
                  item => item.providerWorkspace.role !== null
                ).length,
                tzid: workspace.workspace.region.tzid || '',
                countryCode: workspace.workspace.region.countryCode || '',
                workspaceId: workspace.workspace.id,
                accountCreated: workspace.workspace.createdAt || ''
              }
            : {};
        commit('setWorkspace', {
          role: role,
          staff: workspace.staff,
          fitunePay: null,
          ownerPlan,
          currency: workspace.workspace.currency || {}
        });
      } catch {
        commit('setWorkspace', {
          role: null,
          staff: null,
          fitunePay: null,
          ownerPlan: {},
          currency: {}
        });
      }
    },
    getRoledStaffsLimit({ commit }, limit) {
      commit('setRoledStaffsLimit', limit);
    },
    getActiveUsersLimit({ commit }, limit) {
      commit('setActiveUsersLimit', limit);
    },
    getHasMultipleWorkspaces({ commit }, value) {
      commit('setHasMultipleWorkspaces', value);
    },
    getRooms({ commit }, limit) {
      commit('setRooms', limit);
    },
    getVenuesConnectedWellhub({ commit }, value) {
      commit('setVenuesConnectedWellhub', value);
    },
    getVenuesConnected1Fit({ commit }, value) {
      commit('setVenuesConnected1Fit', value);
    },
    getVenuesConnectedTotalPass({ commit }, value) {
      commit('setVenuesConnectedTotalPass', value);
    },
    getVenuesConnectedFitpass({ commit }, value) {
      commit('setVenuesConnectedFitpass', value);
    },
    getVenuesConnectedGoogleReserve({ commit }, value) {
      commit('setVenuesConnectedGoogleReserve', value);
    },
    getGoogleReserveIntegrationData({ commit }, value) {
      commit('setGoogleReserveIntegrationData', value);
    },
    getStaffSetting({ commit }, value) {
      commit('setStaffSetting', value);
    },
    getFitunePay({ commit }, value) {
      commit('setFitunePay', value);
    },
    getFitunePayCountryCode({ commit }, value) {
      commit('setFitunePayCountryCode', value);
    },
    getFitunePayExpansion({ commit }, value) {
      commit('setFitunePayExpansion', value);
    },
    getVimeo({ commit }, limit) {
      commit('setVimeo', limit);
    },
    getWellhub({ commit }, limit) {
      commit('setWellhub', limit);
    },
    getOneFit({ commit }, limit) {
      commit('setOneFit', limit);
    },
    getTotalPass({ commit }, limit) {
      commit('setTotalPass', limit);
    },
    getFitpass({ commit }, limit) {
      commit('setFitpass', limit);
    },
    getGoogleReserve({ commit }, value) {
      commit('setGoogleReserve', value);
    },
    hideLoader({ commit }) {
      commit('loading', false);
    },
    showLoader({ commit }) {
      commit('loading', true);
    },
    getInfoWebsite({ commit }, info) {
      commit('setWebsiteInfo', info);
    },
    getExtraInfoUser({ commit }, info) {
      commit('setExtraInfoUser', info);
    },
    getUserEmail({ commit }, info) {
      commit('setUserEmail', info);
    },
    getUserFullname({ commit }, info) {
      commit('setUserFullname', info);
    },
    get1On1Tab({ commit }, info) {
      commit('set1On1Tab', info);
    },
    getScheduleDays({ commit }, info) {
      commit('setScheduleDays', info);
    },
    getMaxPosition({ commit }, position) {
      commit('setMaxPosition', position);
    },
    getKiosk({ commit }, kiosk) {
      commit('setKiosk', kiosk);
    },
    getEmbedInfoSeen({ commit }, value) {
      commit('setEmbedInfoSeen', value);
    },
    getInvoiceUrl({ commit }, value) {
      commit('setInvoiceUrl', value);
    },
    getMigrationInProgress({ commit }, value) {
      commit('setMigrationInProgress', value);
    },
    getRevampShellFlag({ commit }, value) {
      commit('setRevampShellFlag', value);
    }
  },
  getters: {
    getStripePublishableKey(state) {
      // Non-Fitune Pay accounts always use the default key.
      if (!state.workspace.fitunePay) return gon.stripePublishableKey;
      // fitunePayCountryCode / fitunePayExpansion are stored alongside fitunePay (Header) so they
      // are available in every bundle; country support lives in shared/fitunePayCountries.js.
      const { fitunePayCountryCode, fitunePayExpansion } = state.workspace;
      return (
        getStripeKeyByCountry(fitunePayCountryCode, fitunePayExpansion) || gon.stripePublishableKey
      );
    },
    showEmailContacts(state) {
      let isStaffSettingNull = state.workspace?.staff?.staffSetting === null;
      return isStaffSettingNull
        ? true
        : state.workspace?.staff?.staffSetting.advancedSettings?.showContacts;
    },
    showStaffSchedules(state) {
      const isStaffSettingNull = state.workspace?.staff?.staffSetting === null;
      const isShowSchedulesNull =
        !isStaffSettingNull &&
        state.workspace?.staff?.staffSetting?.advancedSettings?.showSchedules === null;
      return isStaffSettingNull || isShowSchedulesNull
        ? true
        : state.workspace?.staff?.staffSetting?.advancedSettings?.showSchedules;
    },
    showStaffAnalytics(state) {
      const isStaffSettingNull = state.workspace?.staff?.staffSetting === null;
      const isShowAnalyticsNull =
        !isStaffSettingNull &&
        state.workspace?.staff?.staffSetting?.advancedSettings?.showAnalytics === null;
      return isStaffSettingNull || isShowAnalyticsNull
        ? false
        : state.workspace?.staff?.staffSetting?.advancedSettings?.showAnalytics;
    },
    getBaseUrl(state) {
      return state.baseUrl;
    },
    getApolloClient() {
      return apolloClient;
    },
    getUser(state) {
      return state.user;
    },
    getStepOnboarding(state) {
      return state.stepOnboarding;
    },
    currentDomain(state) {
      let url = state.website.customDomainActive
        ? state.website.customDomainURL
        : state.website.fituneDomainURL;
      if (!url.startsWith('https://') && !url.startsWith('http://')) {
        return 'https://' + url;
      } else {
        return url;
      }
    },
    show1On1(state) {
      return state.show1On1;
    },
    scheduleDays(state) {
      return state.scheduleDays;
    },
    hasFreeForeverPlan(state) {
      const FREE = 'free_forever';
      return state.workspace.ownerPlan.slug === FREE;
    },
    isNewProvider(state) {
      const accountCreated = state.workspace?.ownerPlan?.accountCreated;
      const cutoffTimestamp = Number(
        process.env.VUE_APP_WEBSITE_BUILDER_NEW_PROVIDER_CUTOFF_TIMESTAMP
      );
      if (!accountCreated || !cutoffTimestamp) return false;

      const accountCreatedTimestamp = moment.utc(accountCreated, 'YYYY-MM-DD HH:mm:ss').unix();
      return accountCreatedTimestamp >= cutoffTimestamp;
    },
    hasAccessToVimeo(state) {
      return state.workspace.ownerPlan && state.workspace.ownerPlan.vimeo;
    },
    hasAccessToWellhub(state) {
      return state.workspace.ownerPlan && state.workspace.ownerPlan.wellhubEnabled;
    },
    hasAccessToOneFit(state) {
      return (state.workspace.ownerPlan && state.workspace.ownerPlan.onefitEnabled) ?? false;
    },
    hasAccessToTotalPass(state) {
      return state.workspace.ownerPlan && state.workspace.ownerPlan.totalpassEnabled;
    },
    hasAccessToFitpass(state) {
      return state.workspace.ownerPlan && state.workspace.ownerPlan.fitpassEnabled;
    },
    hasAccessToGoogleReserve(state) {
      return state.workspace.ownerPlan && state.workspace.ownerPlan.googleReserveEnabled;
    },
    ldClient(state) {
      return state.ldClient;
    },
    toggleCalendar(state) {
      return state.toggleCalendar;
    },
    invoiceUrl(state) {
      return state.invoiceUrl;
    },
    migrationInProgress(state) {
      return state.migrationInProgress;
    },
    // True only once the flag has resolved and is on, so a `v-if` never paints
    // the new chrome while the value is still unknown.
    hasRevampShell(state) {
      return state.revampShellFlag === true;
    },
    isRevampShellResolved(state) {
      return state.revampShellFlag !== null;
    }
  },
  modules: {
    cart
  }
};
